import { Card } from "@/src/components/cards";
import { closeDialog, openDialog, openDrawer } from "@/src/components/dialogs";
import { ReorderableItems } from "@/src/components/reorderable";
import { FieldsSection, ScoreInterval, Field } from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import {
  updateOrPush,
  uid,
  escapeId,
  getUserId,
  jsonAbstract,
} from "@/src/utils/utils";
import { useState } from "react";
import { MdOutlineDragIndicator } from "react-icons/md";
import { FieldScoreIntervals } from "../../hospitalisation/scoresHook";
import FieldForm from "./fieldForm";
import { Button } from "@/src/components/buttons";

export default function TableSectionFields({
  section,
  allSections,
  currentValue,
  onChange,
}: {
  section: FieldsSection;
  allSections: (FieldsSection | null | undefined)[];
  currentValue: (Field | null | undefined)[];
  onChange: (newValue: (Field | null | undefined)[]) => void;
}) {
  let [fields, setFields] = useState(currentValue);

  async function openFieldForm(field: Field | null | undefined) {
    if (!field) return;
    await openDrawer({
      title: "Champ",
      content: (
        <FieldForm
          sections={allSections}
          currentData={field}
          onChange={(newValue) => {
            field = newValue;
          }}
        />
      ),
      onConfirm: async () => {
        if (!field) return;
        let inserted = await apiFetch("fields", "addOne", { field });
        if (!inserted) return;
        let data = [...fields];
        data = updateOrPush(data, inserted, (f) => f?.id == inserted?.id);
        setFields(data);
        onChange(data!);
      },
    });
  }

  function removeField(field: Field | null | undefined) {
    if (!field) return;
    setFields([...fields.filter((f) => f?.id != field?.id)]);
    onChange([...fields.filter((f) => f?.id != field?.id)]);
  }

  return (
    <div className="grid c1 gap10" style={{ width: "100%", overflowY: "auto" }}>
      <table>
        <tr>
          <th></th>
          {section.tableColumns?.map((column) => {
            if (!column) return null;
            return (
              <th key={uid()} className="white-bg">
                {column}
              </th>
            );
          })}
        </tr>
        {section.tableRows?.map((row) => {
          return (
            <tr key={uid()}>
              <td className="white-bg">{row}</td>
              {section.tableColumns?.map((column) => {
                if (!column) return null;
                let field = fields?.find(
                  (f) => f?.id == escapeId("fields", `${row}_${column}`)
                );

                let optionsStr = field?.options
                  ?.filter((option) => option)
                  ?.map((option) => option?.name)
                  .join(", ");

                let child = <div>-</div>;

                if (field)
                  child = (
                    <Card
                      key={uid()}
                      color="simple"
                      title={field?.name || "Champ sans nom"}
                      leading={<></>}
                      subTitle={field?.unit?.name}
                      onMore={async () => {
                        await openDialog({
                          id: "fieldActions",
                          title: "Actions",
                          content: (
                            <div className="c1 gap10">
                              <Button
                                type="tertiary"
                                onClick={() => {
                                  let newFields = [...fields];
                                  const currentField = jsonAbstract(field);
                                  newFields = newFields.map((f) => {
                                    if (!f) return f;
                                    return {
                                      ...f,
                                      type: currentField?.type || f.type,
                                      options:
                                        currentField?.options || f.options,
                                    } as Field;
                                  });
                                  setFields(newFields);
                                  onChange(newFields);
                                  closeDialog("fieldActions");
                                }}
                              >
                                Généraliser le type et les options
                              </Button>
                              <Button
                                type="tertiary"
                                color="red"
                                onClick={() => {
                                  removeField(field);
                                  closeDialog("fieldActions");
                                }}
                              >
                                Supprimer
                              </Button>
                            </div>
                          ),
                        });
                      }}
                    >
                      <div className="c1 gap10">
                        <div className="gray">{field?.type}</div>
                        {optionsStr && (
                          <div className="gray">
                            {field?.options?.length} options
                          </div>
                        )}
                      </div>
                    </Card>
                  );

                return (
                  <td
                    key={uid()}
                    className={`pointer ${field != null ? "white-bg" : ""}`}
                    onClick={() => {
                      field ||= {
                        id: escapeId("fields", `${row}_${column}`),
                        name: `${row} - ${column}`,
                        type: "boolean",
                        options: [],
                        placeholder: "",
                        description: "",
                        defaultValue: null,
                        unit: null,
                        creator: getUserId(),
                      };
                      openFieldForm(field);
                    }}
                  >
                    {child}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </table>
    </div>
  );
}
